
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { getOrderTime, getOrderTitle, getDisplayOrderNumber } from '@/utils/getters';
import { SCHOOL_CANCEL_REASONS } from '@/utils/constants';
import Toast from '@/utils/toast';
import DatePicker from '@/components/shared/DatePicker.vue';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import TableHeaders from '@/components/shared/suites/TableHeaders.vue';
import ReasonSelect from './ReasonSelect.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import WarningModal from '../shared/suites/WarningModal.vue';
import RestaurantSelector from '@/components/shared/RestaurantSelector.vue';
import SelectComponent from '@/components/shared/Select.vue';
import TypeToConfirmModal from '@/components/shared/TypeToConfirmModal.vue';

const namespace: string = 'orders';

@Component<BulkActionsModal>({
	components: {
		SelectComponent,
		DatePicker,
		InfoBanner,
		ReasonSelect,
		StandardModal,
		TableHeaders,
		TextButton,
		TypeToConfirmModal,
		WarningModal,
		RestaurantSelector
	}
})
export default class BulkActionsModal extends Vue {
	@Action('fetchOrders', { namespace }) private fetchOrders!: (query: OrderFilterParams & { commitToStore?: boolean }) => Promise<FetchOrdersResponse | undefined>;
	@Action('bulkActions', { namespace }) private bulkActions!: (payload: BulkActionPayload) => BulkActionResponse;
	@Getter('allowBulkComplete', { namespace: 'auth' }) private allowBulkComplete!: boolean;
	@Getter('allowBulkCancel', { namespace: 'auth' }) private allowBulkCancel!: boolean;
	@Getter('isK12Location', { namespace: 'auth' }) private isK12Location!: boolean;

	private selectedDate: string = '';
	private orders: Order[] = [];
	private tableHeaders: TableHeader[] = [
		{ text: this.$t('orders.management.bulk_actions.selected') },
		{ text: this.$t('orders.management.bulk_actions.scheduled_for') },
		{ text: this.$t('orders.management.bulk_actions.order_number') },
		{ text: this.$t('orders.management.bulk_actions.order_details') }
	];
	private selectedOrders: number[] = [];
	private showCompleteWarningModal: boolean = false;
	private showCancelWarningModal: boolean = false;
	private schoolCancelReasons: string[] = SCHOOL_CANCEL_REASONS;
	private cancelReason: { value: string; note: string } = { value: '', note: '' };
	private responseMessage: object = {
		color: '',
		text: '',
		icon: ''
	};
	private showContent: boolean = false;
	private loading: { fetchingOrders: boolean; bulkAction: boolean; loadingCounts: boolean } = {
		fetchingOrders: false,
		bulkAction: false,
		loadingCounts: false
	};
	private selectedRestaurantIds: string[] = [];
	private selectedLocationType: string = 'single';
	private orderCounts: { totalOrders: number; inProgressOrders: number } | null = null;

	/**
	 * Get the staff email from auth state
	 *
	 * @return {string | null}
	 */
	private get staffEmail(): string | null {
		return this.$store.state.auth.staffEmail;
	}

	/**
	 * Check if location type is single
	 *
	 * @return {boolean}
	 */
	private get isSingle(): boolean {
		return this.selectedLocationType === 'single';
	}

	/**
	 * Get location type options for dropdown
	 *
	 * @return {{ text: string; value: string }[]}
	 */
	private get locationTypeOptions(): { text: string; value: string }[] {
		return [
			{ text: this.$t('orders.management.bulk_actions.single_location'), value: 'single' },
			{ text: this.$t('orders.management.bulk_actions.multi_location'), value: 'multi' }
		];
	}

	/**
	 * Get complete orders button text with count
	 *
	 * @return {string}
	 */
	private get completeOrdersButtonText(): string {
		const baseText = this.$t('orders.management.bulk_actions.complete_orders_btn');

		if (!this.isSingle && this.orderCounts?.inProgressOrders !== undefined) {
			const count = this.orderCounts.inProgressOrders;
			const formattedCount = count.toLocaleString();
			const ordersText = this.$tc('orders.management.bulk_actions.orders_in_progress', count, { count: formattedCount });
			return `${baseText} (${ordersText})`;
		}

		return baseText;
	}

	/**
	 * Number of orders targeted by a bulk action, displayed in the confirmation
	 * modals. In multi-location mode the orders are not listed individually, so the
	 * in-progress count for the selected locations and date is used instead of the
	 * table selection.
	 *
	 * @return {number}
	 */
	private get bulkActionCount(): number {
		return this.isSingle ? this.selectedOrders.length : (this.orderCounts?.inProgressOrders ?? 0);
	}

	/**
	 * Fetch in-progress orders based on the selected date range
	 *
	 * @return {Promise<void>}
	 */
	private async getOrders(): Promise<void> {
		// If no date is selected, show error message
		if (!this.selectedDate) {
			Toast.error(this.$t('orders.management.bulk_actions.error_date'));
			return;
		}
		try {
			this.loading.fetchingOrders = true;
			this.orders = (await this.fetchOrders({
				status: JSON.stringify(['in-progress']),
				startDate: this.selectedDate,
				endDate: this.selectedDate,
				commitToStore: false
			}))!.orders;

			// Set all order to selected by default
			this.selectedOrders = this.orders.map(order => order.id);
			this.showContent = true;
		}
		catch (errorMessage) {
			Toast.error(this.$t('orders.management.error_fetching_orders', { errorMessage }));
		}
		finally {
			this.loading.fetchingOrders = false;
		}
	}

	/**
	 * Fetch order counts for multi-location using existing fetchOrders
	 *
	 * @return {Promise<void>}
	 */
	private async fetchMultiLocationOrderCounts(): Promise<void> {
		if (!this.isSingle && this.selectedRestaurantIds.length && this.selectedDate) {
			try {
				this.loading.loadingCounts = true;
				const result = await this.fetchOrders({
					status: JSON.stringify(['in-progress']),
					startDate: this.selectedDate,
					endDate: this.selectedDate,
					restaurantIds: JSON.stringify(this.selectedRestaurantIds),
					countsOnly: true,
					commitToStore: false
				});

				if (result?.statusTotals) {
					const { pending, inProgress, completed } = result.statusTotals;
					this.orderCounts = {
						totalOrders: pending + inProgress + completed,
						inProgressOrders: inProgress
					};
				}
				else {
					this.orderCounts = null;
				}
			}
			catch (errorMessage) {
				Toast.error(this.$t('orders.management.error_fetching_orders', { errorMessage }));
			}
			finally {
				this.loading.loadingCounts = false;
			}
		}
		else {
			this.orderCounts = null;
			this.loading.loadingCounts = false;
		}
	}

	/**
	 * Handle location type change - clear selected restaurants when switching to single location
	 * and clear selected dates to reset picker
	 *
	 * @return {void}
	 */
	private clearAllValues(): void {
		if (this.isSingle) {
			this.selectedRestaurantIds = [];
		}
		// Clear selected date when switching between single and multi
		this.selectedDate = '';
		this.selectedOrders = [];
		this.showContent = false;
		this.orderCounts = null;
		this.loading.loadingCounts = false;
		// Clear any response messages
		this.responseMessage = {
			color: '',
			text: '',
			icon: ''
		};
	}

	/**
	 * Perform the bulk action on the selected orders
	 * Handles both single location and multi-location scenarios
	 *
	 * @param {string} action - action to perform (complete or cancel)
	 * @return {Promise<void>}
	 */
	private async performBulkActions(action: 'complete' | 'cancel'): Promise<void> {
		try {
			this.loading.bulkAction = true;

			// Prepare the bulk actions payload
			const payload: any = {
				action,
				orderIds: this.selectedOrders,
				email: this.staffEmail
			};

			// Add the cancellation reason selected by the operator (K12 only)
			if (action === 'cancel' && this.isK12Location) {
				payload.cancellation_note = this.cancelReason.note;
			}

			// Add filter for multi-location scenario
			if (!this.isSingle) {
				payload.filter = {
					status: ['in-progress'],
					startDate: this.selectedDate,
					endDate: this.selectedDate,
					restaurantIds: this.selectedRestaurantIds,
					idsOnly: true
				};
			}

			const { updatedOrders, unchangedOrders } = await this.bulkActions(payload);

			if (updatedOrders?.length) {
				// Remove updated orders from the list
				this.orders = this.orders.filter(order => !updatedOrders.includes(order.id));

				// Hide table and buttons if no orders left
				if (!this.orders.length) {
					this.showContent = false;
				}
				this.selectedOrders = unchangedOrders;

				// Display message based on the number of orders updated
				// If all orders were updated, display success message
				// If some orders were updated, display partial success message
				if (this.isSingle) {
					// Single location success message
					this.responseMessage = {
						text: this.$t('orders.management.bulk_actions.request_success_message', {
							action: action === 'complete' ? this.$t('orders.management.bulk_actions.request_complete') : this.$t('orders.management.bulk_actions.request_cancel')
						}),
						color: 'primary',
						icon: 'mdi-information-outline'
					};
					// Emit refresh event for single location
					this.$emit('refresh');
				}
				else {
					// Multi-location success message
					this.responseMessage = {
						text: this.$t('orders.management.bulk_actions.request_success_message', {
							numComplete: updatedOrders?.length,
							action: action === 'complete' ? this.$t('orders.management.bulk_actions.request_complete') : this.$t('orders.management.bulk_actions.request_cancel')
						}),
						color: 'primary',
						icon: 'mdi-information-outline'
					};

					// Fetch the new totals for the applied filters for multi-location
					this.fetchMultiLocationOrderCounts();
				}
			}
			// If no orders were updated, display error message, this message is also displayed if there is a server error
			else {
				this.responseMessage = {
					text: this.$t('orders.management.bulk_actions.error_message'),
					color: 'statusRed',
					icon: 'mdi-alert-outline'
				};
			}
		}
		catch (error) {
			this.responseMessage = {
				text: this.$t('orders.management.bulk_actions.error_message'),
				color: 'statusRed',
				icon: 'mdi-alert-outline'
			};
		}
		finally {
			this.closeWarningModals();
			this.loading.bulkAction = false;
		}
	}

	/**
	 * Get the scheduled time of the order depending of its type
	 *
	 * @param {Order} order
	 * @return {string}
	 */
	private getOrderScheduledTime(order: Order): string {
		return getOrderTime(order);
	}

	/**
	 * Get the details of the order depending of its type
	 *
	 * @param {Order} order
	 * @return {string}
	 */
	private getOrderDetails(order: Order): string {
		return getOrderTitle(order);
	}

	/**
	 * Get the order number to display, prefixed with the restaurant's internal identifier.
	 *
	 * @param {Order} order
	 * @return {string}
	 */
	private getOrderNumber(order: Order): string {
		return getDisplayOrderNumber(order.id, order.restaurant_id);
	}

	/**
	 * Update the selected date and reset the selected orders
	 *
	 * @param {string[]} dateRange
	 * @return {void}
	 */
	private handleDateChange(date: string): void {
		this.selectedOrders = [];
		this.selectedDate = date;
		this.fetchMultiLocationOrderCounts();
	}

	/**
	 * Add/remove order from selected orders list
	 *
	 * @param {Order} order
	 * @return {void}
	 */
	private handleOrderSelected(order: Order): void {
		const index = this.selectedOrders.findIndex((orderId: number) => orderId === order.id);
		index === -1 ? this.selectedOrders.push(order.id) : this.selectedOrders.splice(index, 1);
	}

	/**
	 * A check to see if the order is selected, for checkbox in orders table
	 *
	 * @param {number} orderId
	 * @return {boolean}
	 */
	private isSelected(orderId: number): boolean {
		return this.selectedOrders.some((selectedOrderId: number) => selectedOrderId === orderId);
	}

	/**
	 * Close the confirmation modals
	 *
	 * @return {void}
	 */
	private closeWarningModals(): void {
		this.showCompleteWarningModal = false;
		this.showCancelWarningModal = false;
		this.cancelReason = { value: '', note: '' };
	}
}
